import { useState, type FormEvent } from "react";
import { api, ApiError } from "../../api/client";
import { Modal } from "../../components/ui/Modal";
import type { Note } from "../../types";

interface Props {
  note: Note | null; // null = neue Notiz
  onClose: () => void;
  onSaved: () => void;
}

export function NoteForm({ note, onClose, onSaved }: Props) {
  const isEdit = !!note;
  const [title, setTitle] = useState(note?.title ?? "");
  const [content, setContent] = useState(note?.content ?? "");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const payload = { title: title.trim() || undefined, content: content.trim() };
      if (note) {
        await api.put(`/api/notes/${note.id}`, payload);
      } else {
        await api.post("/api/notes", payload);
      }
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Speichern fehlgeschlagen.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      title={isEdit ? "Notiz bearbeiten" : "Neue Notiz"}
      onClose={onClose}
      footer={
        <>
          <button className="btn-secondary" onClick={onClose}>Abbrechen</button>
          <button className="btn-primary" form="note-form" disabled={submitting || !content.trim()}>
            {submitting ? "Speichern…" : "Speichern"}
          </button>
        </>
      }
    >
      <form id="note-form" onSubmit={handleSubmit} className="space-y-4">
        {error && <div className="bg-danger-muted text-danger text-sm rounded-lg px-3 py-2">{error}</div>}

        <div>
          <label className="label">Titel (optional)</label>
          <input
            className="input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="z.B. Erinnerung"
            maxLength={200}
            autoFocus
          />
        </div>
        <div>
          <label className="label">Inhalt *</label>
          <textarea
            className="input"
            rows={8}
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="Deine Notiz…"
            required
          />
        </div>
      </form>
    </Modal>
  );
}
